import React, { useState, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import GroupLists from './GroupLists';
import CreateGroupButton from '../Parts/CreateEntityButton';
import GroupDetailDialog from './GroupDetailDialog';
import getGroup from '../../actions/api/group/getGroup';
import {
  MainBlock,
  BodyBlock,
  TitleWrapper,
  Title,
  BoldText,
  SearchOptionsWrapper,
  SearchField,
  CheckboxWrapper,
  Checkbox,
  DefaultCheckbox,
  CheckboxLabel,
  ListsWrapper,
} from './styles/GroupPageStyles';
import CreateGroupDialog from './CreateGroupDialog';
import { isCompanyAdmin } from '../../lib/tools';

/**
 * グループ管理画面を表示するメインコンポーネント
 *
 * 新規グループの作成ボタンで、新規グループ作成ダイアログを開く。
 * 各グループ項目はクリック可能で、クリックするとそのグループの詳細ダイアログが開く。
 *
 * 使用する主要な Redux ステートは以下：
 * - currentUserRole: 現在ログインしているユーザーの権限
 * - groups: 現在のグループのリスト
 * - companyAssociatedTags: 企業に紐づく全てのタグセット
 * - searchOptions: グループ検索オプション（名前、使用状況など）
 * - apiToken: APIアクセスのためのトークン
 * - groupDetail: 選択されたグループの詳細情報
 */
const GroupPage = () => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const dispatch = useDispatch();

  const {
    currentUserRole,
    groups,
    companyAssociatedTags: allTagSets,
    searchOptions,
    apiToken,
    groupDetail: selectedGroupDetail,
  } = useSelector(state => ({
    currentUserRole: state.main.innerRole,
    groups: state.group.groups,
    companyAssociatedTags: state.master.companyAssociatedTagSets,
    searchOptions: state.group.searchOptions,
    apiToken: state.main.apiToken,
    groupDetail: state.group.groupDetail,
  }));

  const [showDialog, setShowDialog] = useState(false);
  const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
  const { name, isUsable } = searchOptions;
  const searchGroups =
    groups && groups.filter(group => group.name.indexOf(name) > -1);

  const onUpdateOptions = param =>
    dispatch({ type: 'UPDATE_GROUP_SEARCH_OPTIONS', param });

  const onOpenDialog = useCallback(
    groupId => {
      dispatch(getGroup(apiToken, {}, groupId)).then(() => {
        setShowDialog(true);
      });
    },
    [apiToken, dispatch],
  );

  const handleCheckboxChange = event => {
    const isUsableValue = event.target.checked ? null : true;
    onUpdateOptions({ isUsable: isUsableValue });
  };

  const isCompanyAdminUser = isCompanyAdmin(currentUserRole);

  return (
    <MainBlock className="GroupManagementPage">
      <BodyBlock>
        <TitleWrapper>
          <Title>
            <BoldText>
              <FormattedMessage id="group.header.title" />
            </BoldText>
            <SearchOptionsWrapper>
              <FormattedMessage id="group.header.search.name" />
              <SearchField
                id="GroupManagementSearch"
                type="text"
                defaultValue={name}
                placeholder={formatMessage({
                  id: 'group.header.search.placeholder.name',
                })}
                onChange={e => onUpdateOptions({ name: e.target.value })}
              />
              <CheckboxWrapper>
                <Checkbox>
                  <DefaultCheckbox
                    id="SetGroupCheckbox"
                    type="checkbox"
                    checked={isUsable === null}
                    onChange={handleCheckboxChange}
                  />
                  <CheckboxLabel
                    className="checkBoxLabel"
                    htmlFor="SetGroupCheckbox"
                  />
                  <FormattedMessage id="group.header.checkbox.label" />
                </Checkbox>
              </CheckboxWrapper>
            </SearchOptionsWrapper>
          </Title>
          {isCompanyAdminUser && (
            <CreateGroupButton
              onCreate={() => setShowCreateGroupDialog(true)}
              labelId="common.button.add"
            />
          )}
        </TitleWrapper>
        <ListsWrapper>
          <GroupLists groups={searchGroups} onOpenDialog={onOpenDialog} />
        </ListsWrapper>
      </BodyBlock>
      {showDialog && (
        <GroupDetailDialog
          selectedGroup={selectedGroupDetail}
          allTagSets={allTagSets}
          onClose={setShowDialog}
        />
      )}
      {showCreateGroupDialog && (
        <CreateGroupDialog
          group={selectedGroupDetail}
          allTagSets={allTagSets}
          onClose={() => setShowCreateGroupDialog(false)}
        />
      )}
    </MainBlock>
  );
};

export default GroupPage;
